import { Component, OnInit, ViewChild } from '@angular/core';
import { forkJoin } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import Swal from 'sweetalert2';
import { RiderService } from './rider.service';
import { RiderDialogComponent } from './rider-dialog/rider-dialog.component';

@Component({
  selector: 'app-riders',
  templateUrl: './riders.component.html',
  styleUrls: ['./riders.component.scss']
})
export class RidersComponent implements OnInit {
  displayedColumns: string[] = [
    'fullName',
    'phoneNumber',
    'assignedPhoneModel',
    'assignedPhoneNumber',
    'startPhoneState',
    'startCreditAmount',
    'endCreditAmount',
    'creditUsed',
    'isActive',
    'phoneActions',
    'actions'
  ];

  dataSource = new MatTableDataSource<any>([]);
  loading = false;
  analytics: any = null;

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(private riderService: RiderService, private dialog: MatDialog) {}

  ngOnInit(): void {
    this.loadRiders();
  }

  loadRiders(): void {
    this.loading = true;
    forkJoin({
      riders: this.riderService.getRiders(),
      analytics: this.riderService.getPhoneAnalytics()
    }).subscribe({
      next: ({ riders, analytics }) => {
        this.dataSource.data = riders;
        this.dataSource.paginator = this.paginator;
        this.analytics = analytics;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        console.error('Failed to load riders', err);
        Swal.fire('Error', 'Failed to load riders', 'error');
      }
    });
  }

  openDialog(rider?: any): void {
    const dialogRef = this.dialog.open(RiderDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      disableClose: true,
      panelClass: 'app-modal-panel',
      backdropClass: 'app-modal-backdrop',
      data: rider || null
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) this.loadRiders();
    });
  }

  toggleStatus(rider: any): void {
    const updated = { ...rider, isActive: !rider.isActive };
    this.riderService.updateRider(rider.id, updated).subscribe({
      next: () => {
        Swal.fire('Updated', 'Rider status updated successfully.', 'success');
        this.loadRiders();
      },
      error: () => Swal.fire('Error', 'Failed to update status.', 'error')
    });
  }

  deleteRider(id: number): void {
    Swal.fire({
      title: 'Are you sure?',
      text: 'This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it'
    }).then((res) => {
      if (res.isConfirmed) {
        this.riderService.deleteRider(id).subscribe({
          next: () => {
            Swal.fire('Deleted', 'Rider removed successfully.', 'success');
            this.loadRiders();
          },
          error: () => Swal.fire('Error', 'Failed to delete rider.', 'error')
        });
      }
    });
  }

  async openPhoneWork(rider: any): Promise<void> {
    const result = await Swal.fire({
      title: `Open Work Phone: ${rider.fullName}`,
      html: `
        <input id="phone-state" class="swal2-input" placeholder="Phone state (e.g. Good)">
        <input id="start-credit" class="swal2-input" placeholder="Start credit amount" type="number" step="0.01" min="0">
      `,
      icon: 'warning',
      text: 'Please verify phone state and start credit before saving.',
      showCancelButton: true,
      confirmButtonText: 'Save Start Record',
      preConfirm: () => {
        const phoneState = (document.getElementById('phone-state') as HTMLInputElement)?.value?.trim();
        const credit = Number((document.getElementById('start-credit') as HTMLInputElement)?.value);
        if (!phoneState) {
          Swal.showValidationMessage('Phone state is required.');
          return false;
        }
        if (Number.isNaN(credit) || credit < 0) {
          Swal.showValidationMessage('Enter valid start credit.');
          return false;
        }
        return { phoneState, creditAmount: credit };
      }
    });

    if (!result.isConfirmed) return;

    this.riderService.openPhoneWork(rider.id, result.value).subscribe({
      next: () => {
        Swal.fire('Saved', 'Phone start record captured.', 'success');
        this.loadRiders();
      },
      error: (err) => Swal.fire('Error', err?.error?.message || 'Failed to save phone start record.', 'error')
    });
  }

  async closePhoneWork(rider: any): Promise<void> {
    const result = await Swal.fire({
      title: `Close Work Phone: ${rider.fullName}`,
      input: 'number',
      inputLabel: 'End credit amount',
      inputAttributes: { min: '0', step: '0.01' },
      icon: 'warning',
      text: 'Please verify closing credit before saving.',
      showCancelButton: true,
      confirmButtonText: 'Save Close Record',
      preConfirm: (value) => {
        const credit = Number(value);
        if (Number.isNaN(credit) || credit < 0) {
          Swal.showValidationMessage('Enter valid end credit.');
          return false;
        }
        return { creditAmount: credit };
      }
    });

    if (!result.isConfirmed) return;

    this.riderService.closePhoneWork(rider.id, result.value).subscribe({
      next: () => {
        Swal.fire('Saved', 'Phone close record captured.', 'success');
        this.loadRiders();
      },
      error: (err) => Swal.fire('Error', err?.error?.message || 'Failed to save phone close record.', 'error')
    });
  }

  async editPhoneLog(rider: any): Promise<void> {
    if (!rider.phoneLogId) return;

    const result = await Swal.fire({
      title: `Edit Phone Log: ${rider.fullName}`,
      html: `
        <input id="edit-phone-state" class="swal2-input" placeholder="Phone state" value="${rider.startPhoneState || ''}">
        <input id="edit-start-credit" class="swal2-input" placeholder="Start credit" type="number" step="0.01" min="0" value="${rider.startCreditAmount ?? ''}">
        <input id="edit-end-credit" class="swal2-input" placeholder="End credit" type="number" step="0.01" min="0" value="${rider.endCreditAmount ?? ''}">
      `,
      icon: 'warning',
      text: 'Edits are only allowed within 15 minutes after insert.',
      showCancelButton: true,
      confirmButtonText: 'Update',
      preConfirm: () => {
        const state = (document.getElementById('edit-phone-state') as HTMLInputElement)?.value?.trim();
        const startRaw = (document.getElementById('edit-start-credit') as HTMLInputElement)?.value;
        const endRaw = (document.getElementById('edit-end-credit') as HTMLInputElement)?.value;

        const payload: any = {};
        if (state) payload.startPhoneState = state;
        if (startRaw !== '') payload.startCreditAmount = Number(startRaw);
        if (endRaw !== '') payload.endCreditAmount = Number(endRaw);

        if (Object.keys(payload).length === 0) {
          Swal.showValidationMessage('No fields to update.');
          return false;
        }

        return payload;
      }
    });

    if (!result.isConfirmed) return;

    this.riderService.editPhoneLog(rider.phoneLogId, result.value).subscribe({
      next: () => {
        Swal.fire('Updated', 'Phone log updated successfully.', 'success');
        this.loadRiders();
      },
      error: (err) => Swal.fire('Error', err?.error?.message || 'Failed to update phone log.', 'error')
    });
  }
}
